import {
  DOMAIN_PERMISSIONS_TEST_EMAIL,
  DOMAIN_PERMISSIONS_TEST_ROLES,
  GOD_ROLE,
  ADMIN_ROLE,
  AGENT_ROLE,
} from '../constants/system'

import { DomainPermissions } from '../types/app'

const DEFAULT_PERMISSIONS: DomainPermissions = {
  isRoot: false,
  canAccessExecutiveReports: false,
  // DataViz is visible by default (#1079, interim).
  //
  // This map is an email-domain allowlist with three entries, so every
  // customer outside bayesprice.com / sky.uk / sky.ie was hidden from the
  // dashboards area regardless of what they are licensed for — and a session
  // whose email does not resolve to a domain fell here too, which is the one
  // way a Sky user could still lose the menu item.
  //
  // Flipping it grants no access that was not already reachable: there is NO
  // server-side counterpart to this flag — `/dashboards/:customer` is
  // `requireAuthMiddleware` only — so the gate hid navigation without
  // protecting the endpoint behind it. That hole is #1081 and is the real
  // fix; entitlement-driven visibility is #1079. Both need a server change,
  // so this is deliberately a stopgap: one line, reversible, and it stops
  // hiding a licensed feature from the people paying for it.
  //
  // A customer with no dashboards sees an empty DataViz list, which is
  // honest — "nothing here yet" rather than "this feature does not exist".
  canAccessDashboards: true,
  canAccessAutocoder: false,
  canAccessFiles: true,
  canAccessAdmin: true,
  canAccessThemeAI: false,
  canAccessMultiTableMode: true,
  canAccessSocialMediaPOC: false,
  canAccessAgent: false,
}

export const DOMAIN_CONFIG: Record<string, DomainPermissions> = {
  'bayesprice.com': {
    isRoot: true,
    canAccessExecutiveReports: true,
    canAccessDashboards: true,
    canAccessAutocoder: true,
    canAccessFiles: true,
    canAccessAdmin: true,
    canAccessThemeAI: true,
    canAccessMultiTableMode: true,
    canAccessSocialMediaPOC: true,
    canAccessAgent: true,
  },
  'sky.uk': {
    isRoot: false,
    canAccessExecutiveReports: false,
    canAccessDashboards: true,
    canAccessAutocoder: false,
    canAccessFiles: true,
    canAccessAdmin: false,
    canAccessThemeAI: true,
    canAccessMultiTableMode: false,
    canAccessSocialMediaPOC: true,
    canAccessAgent: false,
  },
  'sky.ie': {
    isRoot: false,
    canAccessExecutiveReports: false,
    canAccessDashboards: true,
    canAccessAutocoder: false,
    canAccessFiles: true,
    canAccessAdmin: false,
    canAccessThemeAI: true,
    canAccessMultiTableMode: false,
    canAccessSocialMediaPOC: true,
    canAccessAgent: false,
  }
}

// this turns a carbon role into a list of domain permissions it grants
// e.g. if the carbon role has "Manager", "Realm" or "Admin" then they can see the admin page
export const CARBON_ROLE_PERMISSION_MAP: Record<string, Partial<DomainPermissions>> = {
  [GOD_ROLE]: {
    canAccessAdmin: true,
    canAccessAgent: true,
  },
  [ADMIN_ROLE]: {
    canAccessAdmin: true,
  },
  'Admin': {
    canAccessAdmin: true,
  },
  [AGENT_ROLE]: {
    canAccessAgent: true,
  },
}


// Lowercased view of CARBON_ROLE_PERMISSION_MAP so role grants match regardless
// of case. Callers (e.g. account.tsx) lowercase roles before lookup, while the
// map keys come from capitalised constants like GOD_ROLE ('Manager') / AGENT_ROLE
// ('Agent') — without normalising, every role grant silently fell through.
const CARBON_ROLE_PERMISSION_MAP_LOWER: Record<string, Partial<DomainPermissions>> =
  Object.fromEntries(
    Object.entries(CARBON_ROLE_PERMISSION_MAP).map(([role, perms]) => [role.toLowerCase(), perms]),
  )

/**
 * Extract domain from email address
 */
export const extractDomain = (email: string): string => {
  if (!email || !email.includes('@')) return ''
  return email.split('@')[1].toLowerCase()
}

/**
 * Get permissions for a given email address
 * First checks if email is mapped to a specific domain, then falls back to domain-based lookup
 */
export const getPermissionsForEmail = (email: string, roles: string[] = []): DomainPermissions => {
  const queryParams = new URLSearchParams(window.location.search)
  let useEmail = email
  let useRoles = roles
  if (process.env.NODE_ENV === 'development' && queryParams.get(DOMAIN_PERMISSIONS_TEST_EMAIL)) useEmail = queryParams.get(DOMAIN_PERMISSIONS_TEST_EMAIL) || ''
  if (process.env.NODE_ENV === 'development' && queryParams.get(DOMAIN_PERMISSIONS_TEST_ROLES)) useRoles = (queryParams.get(DOMAIN_PERMISSIONS_TEST_ROLES) || '').split(',')

  const domain = extractDomain(useEmail)
  const domainPermissions = DOMAIN_CONFIG[domain] || DEFAULT_PERMISSIONS

  // get the hoisted permissions from the carbon roles (case-insensitive)
  const carbonRolePermissions = useRoles.map(role => CARBON_ROLE_PERMISSION_MAP_LOWER[role.toLowerCase()] || {}).reduce((acc, curr) => ({
    ...acc,
    ...curr,
  }), {})

  return {
    ...domainPermissions,
    ...carbonRolePermissions,
  }
}

/**
 * Check if user can access a specific feature
 */
export const canUserAccess = (email: string, feature: keyof DomainPermissions, roles?: string[]): boolean => {
  const permissions = getPermissionsForEmail(email, roles)
  return permissions[feature] as boolean
}
